package com.eladfinish.bible.contest.ui.components.toolbar.autohide

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.CubicBezierEasing
import androidx.compose.animation.core.Easing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.unit.Constraints
import kotlin.math.roundToInt

// The curves of Chrome for Android (ui/gfx/animation/tween.cc: ACCEL_30_DECEL_LIN, ACCEL_LIN_DECEL_60)
private val HideEasing = CubicBezierEasing(0.3f, 0f, 1f, 1f)
private val ShowEasing = CubicBezierEasing(0f, 0f, 0.4f, 1f)

private fun ToolbarAutoHideEasing.toEasing(): Easing = when (this) {
    ToolbarAutoHideEasing.HIDE -> HideEasing
    ToolbarAutoHideEasing.SHOW -> ShowEasing
    ToolbarAutoHideEasing.SHOW_NEAR_TOP -> LinearEasing
}

/**
 * The [toolbar] above the [content], which takes all the height the toolbar leaves.
 *
 * Both are measured here, in one layout pass, so the content's top always touches the toolbar's bottom.
 * In the scaffold's top bar the toolbar would move first and the content follow through the scaffold's
 * padding a few frames later, showing the background between them for as long as the toolbar hides.
 */
@Composable
internal fun ToolbarAboveContent(
    toolbarAutoHide: ToolbarAutoHide,
    toolbar: @Composable () -> Unit,
    content: @Composable () -> Unit,
) {
    val transition by toolbarAutoHide.transition.collectAsState()
    val shownFraction = remember { Animatable(if (transition.isHidden) 0f else 1f) }

    LaunchedEffect(transition) {
        shownFraction.animateTo(
            targetValue = if (transition.isHidden) 0f else 1f,
            animationSpec = tween(durationMillis = transition.durationMs, easing = transition.easing.toEasing()),
        )
    }

    Layout(
        content = {
            Box { toolbar() }
            Box { content() }
        },
        modifier = Modifier.clipToBounds(),
    ) { (toolbarMeasurable, contentMeasurable), constraints ->
        val toolbarPlaceable = toolbarMeasurable.measure(constraints.copy(minHeight = 0, maxHeight = Constraints.Infinity))
        val toolbarHeight = toolbarPlaceable.height
        toolbarAutoHide.toolbarHeightPx = toolbarHeight.toFloat()

        val fraction = shownFraction.value
        val shownHeight = (toolbarHeight * fraction).roundToInt()
        val contentHeight = (constraints.maxHeight - shownHeight).coerceAtLeast(0)
        val contentPlaceable = contentMeasurable.measure(constraints.copy(minHeight = contentHeight, maxHeight = contentHeight))

        layout(constraints.maxWidth, constraints.maxHeight) {
            contentPlaceable.place(0, shownHeight)
            // Not placed when hidden: no touch reaches it and TalkBack does not read it
            if (fraction > 0f) {
                toolbarPlaceable.place(0, shownHeight - toolbarHeight)
            }
        }
    }
}
